import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { clientsService } from '@/features/clients/services/clients.service';
import { ClientList } from '@/features/clients/components/ClientList';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/ui/page-header';
import { optStr, optPage, optEnum, useUrlText } from '@/lib/list-search';
import { ClientForm } from '@/features/clients/components/ClientForm';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { companiesService } from '@/features/companies/services/companies.service';
import { Pagination } from '@/components/ui/pagination';

const CLIENTS_PER_PAGE = 20;

export interface ClientsSearch {
  search?: string;
  active?: 'true' | 'false';
  companies?: string; // ids separados por vírgula
  type?: 'internal' | 'external';
  page?: number;
}

export const validateClientsSearch = (s: Record<string, unknown>): ClientsSearch => ({
  search: optStr(s.search),
  active: optEnum(String(s.active), ['true', 'false'] as const),
  companies: optStr(s.companies),
  type: optEnum(s.type, ['internal', 'external'] as const),
  page: optPage(s.page),
});

export const Route = createFileRoute('/_authenticated/clients/')({
  validateSearch: validateClientsSearch,
  component: ClientsPage,
});

export function ClientsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const urlSearch = Route.useSearch();
  const searchNavigate = Route.useNavigate();
  const search = urlSearch.search ?? '';
  const isActiveFilter =
    urlSearch.active === undefined ? undefined : urlSearch.active === 'true';
  const companyIdsFilter = useMemo(
    () => (urlSearch.companies ? urlSearch.companies.split(',') : []),
    [urlSearch.companies],
  );
  const clientTypeFilter = urlSearch.type ?? 'all';
  const page = urlSearch.page ?? 1;
  // mudar filtro volta para a página 1
  const setParams = (patch: Partial<ClientsSearch>) =>
    searchNavigate({
      search: (prev) => ({ ...prev, page: undefined, ...patch }),
      replace: true,
    });
  const [searchInput, setSearchInput] = useUrlText(urlSearch.search, (v) =>
    setParams({ search: v }),
  );
  const setIsActiveFilter = (v: boolean | undefined) =>
    setParams({ active: v === undefined ? undefined : v ? 'true' : 'false' });
  const setCompanyIdsFilter = (ids: string[]) =>
    setParams({ companies: ids.length ? ids.join(',') : undefined });
  const setClientTypeFilter = (v: 'all' | 'internal' | 'external') =>
    setParams({ type: v === 'all' ? undefined : v });

  const { data: clients = [], isLoading } = useQuery({
    queryKey: [
      'clients-list',
      search,
      isActiveFilter,
      companyIdsFilter,
      clientTypeFilter,
    ],
    queryFn: () =>
      clientsService.listClients({
        search: search || undefined,
        isActive: isActiveFilter,
        companyIds: companyIdsFilter.length ? companyIdsFilter : undefined,
        clientType:
          clientTypeFilter === 'all' ? undefined : clientTypeFilter,
      }),
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', 'with-clients-count'],
    queryFn: () =>
      companiesService.listCompanies({
        includeClientsCount: true,
      }),
  });

  const createMutation = useMutation({
    mutationFn: clientsService.createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients-list'] });
      toast({ title: 'Cliente criado com sucesso' });
      setCreateDialogOpen(false);
      navigate({ to: `/clients/${data.id}` });
    },
  });

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
    });
  }, [clients]);

  const paginatedClients = useMemo(() => {
    const start = (page - 1) * CLIENTS_PER_PAGE;
    const end = start + CLIENTS_PER_PAGE;
    return sortedClients.slice(start, end);
  }, [page, sortedClients]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clientes"
        description="Lista de clientes cadastrados."
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo cliente
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <CardTitle>Lista de Clientes</CardTitle>
            <div className="flex-1 max-w-sm">
              <Input
                placeholder="Buscar por nome, CNPJ ou nome fantasia..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {companyIdsFilter.length > 0
                      ? `Empresas (${companyIdsFilter.length})`
                      : 'Filtrar Empresas'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  {companies.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhuma empresa encontrada
                    </div>
                  ) : (
                    companies.map((company) => (
                      <DropdownMenuCheckboxItem
                        key={company.id}
                        checked={companyIdsFilter.includes(company.id)}
                        onCheckedChange={(checked) => {
                          setCompanyIdsFilter(
                            checked
                              ? [...companyIdsFilter, company.id]
                              : companyIdsFilter.filter((id) => id !== company.id),
                          );
                        }}
                        onSelect={(event) => event.preventDefault()}
                      >
                        {company.name} ({company.clientsCount ?? 0})
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {companyIdsFilter.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCompanyIdsFilter([])}
                >
                  Limpar empresas
                </Button>
              )}
              <Button
                variant={isActiveFilter === undefined ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsActiveFilter(undefined)}
              >
                Todos
              </Button>
              <Button
                variant={isActiveFilter === true ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsActiveFilter(true)}
              >
                Ativos
              </Button>
              <Button
                variant={isActiveFilter === false ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsActiveFilter(false)}
              >
                Inativos
              </Button>
              <Button
                variant={clientTypeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setClientTypeFilter('all')}
              >
                Todas categorias
              </Button>
              <Button
                variant={clientTypeFilter === 'internal' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setClientTypeFilter('internal')}
              >
                Interno
              </Button>
              <Button
                variant={clientTypeFilter === 'external' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setClientTypeFilter('external')}
              >
                Externo
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ClientList
            clients={paginatedClients}
            isLoading={isLoading}
            onSelect={(id) => navigate({ to: `/clients/${id}` })}
          />
          <Pagination
            className="mt-4"
            total={sortedClients.length}
            page={page}
            limit={CLIENTS_PER_PAGE}
            onPageChange={(p) => setParams({ page: p > 1 ? p : undefined })}
          />
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar Cliente</DialogTitle>
          </DialogHeader>
          <ClientForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setCreateDialogOpen(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
